import React, { useCallback, useEffect, useMemo } from 'react';

import './App.css';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from '@mui/material';

import { allApplications, ApplicationType, IApplication, Loading, ModalProvider, NotificationPopup } from '@shared/ui';
import {
  AuthorizationContextProvider,
  notificationStore,
  useAuthorizationContext,
  ServerConfigContextProvider,
  useUserStore,
} from 'hpacademy-data-authentication';

import { ConfigProvider } from 'antd';

import { STYLE_TOKENS } from '@shared/ui';
import { theme } from '../stores/theme';

import { PortalApplication } from './PortalApplication';

//TODO: update right export and import as microservices, the current only for poc
import LoginPage from './LoginPage';
import { PublicApp } from './PublicApp';
import { initApiClient } from '@homework-studio/api-client';
import { GlobalStyles } from "@mui/material";
import { AppProvider } from '../queries/provider';

//ready applications
const applicationComponents = new Map<ApplicationType, React.ComponentType<object>>([
  ['classManagements', React.lazy(() => import('hpacademy-homework-studio'))],
  ['userSettings', React.lazy(() => import('@homework-studio/hpacademy/user-setting'))],
  ['homeworkCenters', React.lazy(() => import('@homework-studio/homework-center'))],
  ['studentStudio', React.lazy(() => import('@homework-studio/student-studio'))],
  ['portal', PortalApplication],
]);

export const createAppComponent = (app: IApplication) => {
  const Component = applicationComponents.get(app.id);

  if (!Component) {
    // TODO: Returns the fallback UI (application not found or something, or redirect to the dashboard)
    return null;
  }

  return <Component />;
};

function App() {
  const {
    state: { user },
  } = useAuthorizationContext();
  const { setAccessToken, clearAuth } = useUserStore();

  useEffect(() => {
    initApiClient({
      baseURL: import.meta.env.VITE_BASE_URL_API,
      getAccessToken: () => useUserStore.getState().user?.accessToken,
      setAccessToken: accessToken => setAccessToken(accessToken),
      onUnauthorized: () => {
        clearAuth();
        if (window.location.pathname !== '/') {
          window.location.replace('/');
        }
      },
    });
  }, [clearAuth, setAccessToken]);

  const filteredApplicationsByRole = useMemo(() => {
    if(!user?.userInfo?.role) return [];

    return Object.values(allApplications)
      .filter(app => applicationComponents.has(app.id))
      .filter(app => (app.roles && (app.roles?.includes('all') || app.roles?.includes(user?.userInfo.role))));
  }, [user]);

  return (
    <AppProvider>
      <ServerConfigContextProvider>
        <React.Suspense fallback={<Loading />}>
          <BrowserRouter>
            <Routes>
              {filteredApplicationsByRole.map(app => (
                <Route key={app.id} path={`${app.basePath}/*`} element={createAppComponent(app)} />
              ))}
              <Route index element={createAppComponent(allApplications.portal)} />
              <Route path='login' element={<LoginPage />} />
              <Route path='*' element={createAppComponent(allApplications.portal)} />
            </Routes>
          </BrowserRouter>
        </React.Suspense>
      </ServerConfigContextProvider>
    </AppProvider>
  );
}

export const RootApp = () => {
  const { open, type, msg, delayTime, triggerNotification, destroyNotification } = notificationStore();

  const handleClose = useCallback(() => {
    destroyNotification(false);
  }, [destroyNotification]);

  return (
    <>
      <GlobalStyles
        styles={{
          'html, body, #root': {
            height: '100%',
          },
          body: {
            margin: 0,
            fontFamily: STYLE_TOKENS.font.family.body,
            backgroundColor: STYLE_TOKENS.color.background.canvas,
            color: STYLE_TOKENS.color.text.primary,
          },
          "::-webkit-scrollbar": {
            width: "6px",
            height: "4px",
          },
          "::-webkit-scrollbar-thumb": {
            background: "#aaa",
            borderRadius: "10px",
          },
          "::-webkit-scrollbar-thumb:hover": {
            background: "#888",
          },
        }}
      />

      <AuthorizationContextProvider FallbackApp={<PublicApp />}>
        <ConfigProvider
          theme={{
            token: {
              fontFamily: STYLE_TOKENS.font.family.body,
              colorPrimary: STYLE_TOKENS.color.brand.primary,
              colorText: STYLE_TOKENS.color.text.primary,
              colorTextSecondary: STYLE_TOKENS.color.text.secondary,
              colorBgBase: STYLE_TOKENS.color.background.canvas,
              colorBorder: STYLE_TOKENS.color.border.subtle,
            },
          }}
        >
          <ThemeProvider theme={theme}>
            <ModalProvider>
              <App />
            </ModalProvider>
          </ThemeProvider>
        </ConfigProvider>
      </AuthorizationContextProvider>
      <NotificationPopup open={open} type={type} msg={msg} delayTime={delayTime} close={() => handleClose()} />
    </>
  );
};
